import React, { useEffect, useRef, useState } from "react";
import { X, PaperPlaneRight } from "@phosphor-icons/react";

// Must match CHAT_MAX_LEN in backend/game_ws.py.
export const CHAT_MAX_LEN = 280;

function formatTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** Floating text chat between the two players of a match. */
export default function ChatPanel({ open, onClose, messages, myId, oppName, onSend, connected }) {
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the newest message in view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length]);

  useEffect(() => {
    if (open) inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (onSend(text)) setDraft("");
  };

  return (
    <div
      className="absolute z-30 inset-x-2 bottom-2 h-[55%] md:inset-x-auto md:right-4 md:bottom-4 md:w-80 md:h-[22rem]
                 flex flex-col rounded-2xl border border-border bg-surface/95 backdrop-blur-xl shadow-xl"
      data-testid="chat-panel"
    >
      <div className="flex-none flex items-center justify-between px-4 h-11 border-b border-border">
        <span className="font-display font-bold text-sm truncate">Chat with {oppName}</span>
        <button
          type="button"
          onClick={onClose}
          className="-mr-2 p-2 rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Close chat"
          data-testid="chat-close-btn"
        >
          <X size={16} />
        </button>
      </div>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="chat-messages">
        {messages.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center mt-6">No messages yet. Say hi!</p>
        ) : (
          messages.map((m) => {
            const mine = m.from === myId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm break-words whitespace-pre-wrap ${
                    mine ? "bg-brand text-brand-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md"
                  }`}
                  title={formatTime(m.ts)}
                >
                  {m.text}
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={submit} className="flex-none flex items-center gap-2 p-2 border-t border-border">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, CHAT_MAX_LEN))}
          maxLength={CHAT_MAX_LEN}
          placeholder={connected ? "Message" : "Reconnecting…"}
          disabled={!connected}
          enterKeyHint="send"
          className="flex-1 min-w-0 h-9 px-3 rounded-full bg-background border border-border text-base md:text-sm
                     focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-60"
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={!connected || !draft.trim()}
          className="flex-none w-9 h-9 rounded-full bg-brand text-brand-foreground flex items-center justify-center disabled:opacity-40"
          aria-label="Send"
          data-testid="chat-send-btn"
        >
          <PaperPlaneRight size={16} weight="fill" />
        </button>
      </form>
    </div>
  );
}
